import React, { useEffect } from 'react';
import Button from '../../BaseComponents/Button';
import { useFormikContext } from 'formik';
import { StepProps } from '../../../pages/complete-profile';
import {
  isScanOrPlusMembership,
  OnboardingFormValues,
  shouldShowFemaleSexHormonesStep
} from '../../../types/membersTypes';
import YesNoDescribeQuestion from './YesNoDescribeQuestion';
import './FormQuestion.scss';

type MedicalHistoryStepProps = {
  setCurrentStep: (step: StepProps) => void;
  saveStepData?: (stepNumber: number) => void;
  populateFormWithSavedData?: () => void;
  lastCompletedStep?: number;
  membershipType?: string | null;
};

const MedicalHistoryStep: React.FC<MedicalHistoryStepProps> = ({
  setCurrentStep,
  saveStepData,
  populateFormWithSavedData,
  membershipType
}) => {
  const { values, setFieldError } = useFormikContext<OnboardingFormValues>();
  const isScanOrPlus = isScanOrPlusMembership(membershipType);

  const [hasPopulated, setHasPopulated] = React.useState(false);

  useEffect(() => {
    if (populateFormWithSavedData && !hasPopulated) {
      populateFormWithSavedData();
      setHasPopulated(true);
    }
  }, [populateFormWithSavedData, hasPopulated]);

  const validateStepFields = () => {
    const validationErrors: Record<string, string> = {};

    const requireAnswer = (
      answerField: keyof OnboardingFormValues,
      detailField: keyof OnboardingFormValues,
      answerMessage: string
    ) => {
      const answer = values[answerField] as string | undefined;
      if (answer !== 'yes' && answer !== 'no') {
        validationErrors[answerField as string] = answerMessage;
        setFieldError(answerField as string, answerMessage);
        return;
      }

      if (answer === 'yes') {
        const detail = String(values[detailField] ?? '').trim();
        if (!detail) {
          validationErrors[detailField as string] = 'Please provide more details';
          setFieldError(detailField as string, 'Please provide more details');
        }
      }
    };

    requireAnswer(
      'has_medical_conditions',
      'medical_conditions_list',
      'Please select yes or no'
    );
    requireAnswer('has_surgeries', 'surgeries_details', 'Please select yes or no');
    requireAnswer(
      'has_medications_supplements',
      'supplements',
      'Please select yes or no'
    );
    requireAnswer('has_allergies', 'allergies', 'Please select yes or no');

    return validationErrors;
  };

  const goToTheNextStep = async () => {
    const validationErrors = validateStepFields();

    if (Object.keys(validationErrors).length > 0) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    const isFemale = values.gender?.key === 'female';
    const hasHormonesStep = shouldShowFemaleSexHormonesStep(isFemale);
    const medicalHistoryStepNumber = hasHormonesStep ? 4 : 3;

    try {
      if (saveStepData) {
        await saveStepData(medicalHistoryStepNumber);
      }
    } catch {
      return;
    }

    if (isScanOrPlus) {
      setCurrentStep({
        number: hasHormonesStep ? 5 : 4,
        name: 'MRI Scan Safety and Clinical Considerations'
      });
    } else {
      setCurrentStep({ number: hasHormonesStep ? 5 : 4, name: 'Goals' });
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className='step-3'>
      <YesNoDescribeQuestion
        title='Do you have any past or ongoing medical conditions we should know about?'
        answerField='has_medical_conditions'
        detailField='medical_conditions_list'
        detailPlaceholder='Describe your medical conditions'
      />
      <YesNoDescribeQuestion
        title='Have you had any previous surgeries or medical procedures?'
        answerField='has_surgeries'
        detailField='surgeries_details'
        detailPlaceholder='Describe your surgeries or medical procedures'
      />
      <YesNoDescribeQuestion
        title='Are you currently taking any medications or supplements?'
        answerField='has_medications_supplements'
        detailField='supplements'
        detailPlaceholder='Enter regular medications or supplements'
      />
      <YesNoDescribeQuestion
        title='Do you have any known allergies? If yes, please list the allergen and describe the reaction (e.g. penicillin, anaphylaxis; latex, skin reaction; contrast dye, nausea).'
        answerField='has_allergies'
        detailField='allergies'
        detailPlaceholder='List the allergen and describe the reaction'
      />

      <div className='submit-btn-wrapper'>
        <Button
          text='Back'
          onClick={() => {
            const isFemale = values.gender?.key === 'female';
            if (shouldShowFemaleSexHormonesStep(isFemale)) {
              setCurrentStep({ number: 3, name: 'Required: Female Sex Hormones' });
            } else {
              setCurrentStep({ number: 2, name: 'Current Lifestyle' });
            }
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          backgroundColor={'#ffffff'}
          color={'#2D334E'}
        />
        <Button text='Next' onClick={goToTheNextStep} />
      </div>
    </div>
  );
};

export default MedicalHistoryStep;
